/**
 * Job Posting Form Component
 * Allows companies to create and edit job postings
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Briefcase, 
  MapPin, 
  Clock, 
  DollarSign, 
  Users, 
  BookOpen,
  Plus,
  X,
  Save,
  Loader2,
  AlertCircle,
  Calendar,
  Building,
  Target,
  CheckCircle
} from 'lucide-react';
import { useCompanyAuth } from '../../../core/contexts/CompanyAuthContext';
import { createJobPosting, updateJobPosting } from '../services/companyService';

/**
 * Job Posting Form Component
 * 
 * @param {Object} props - Component props
 * @param {Object} props.existingJob - Existing job data for editing (optional)
 * @param {Function} props.onSaved - Callback when job is saved
 * @param {Function} props.onCancel - Callback when form is cancelled
 * @returns {JSX.Element} Job posting form
 */
const JobPostingForm = ({ existingJob = null, onSaved, onCancel }) => {
  const navigate = useNavigate();
  const { companyProfile, isVerified } = useCompanyAuth();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    requirements: {
      education: '',
      experience: '',
      skills: [],
      other: ''
    },
    location: '',
    jobType: 'full-time',
    experienceLevel: 'entry-level',
    salaryRange: {
      min: '',
      max: '',
      currency: 'ZAR',
      period: 'monthly'
    },
    benefits: [],
    applicationDeadline: '',
    startDate: '',
    isRemote: false,
    category: '',
    department: '',
    contactEmail: '',
    applicationInstructions: '',
    companyDescription: '',
    tags: []
  });

  // Job types
  const jobTypes = [
    { value: 'full-time', label: 'Full-time' },
    { value: 'part-time', label: 'Part-time' },
    { value: 'contract', label: 'Contract' },
    { value: 'internship', label: 'Internship' },
    { value: 'graduate-program', label: 'Graduate Program' },
    { value: 'temporary', label: 'Temporary' }
  ];

  // Experience levels
  const experienceLevels = [
    { value: 'entry-level', label: 'Entry Level (0-2 years)' },
    { value: 'mid-level', label: 'Mid Level (2-5 years)' },
    { value: 'senior-level', label: 'Senior Level (5+ years)' },
    { value: 'executive', label: 'Executive Level' },
    { value: 'student', label: 'Student/Graduate' }
  ];

  // Job categories
  const jobCategories = [
    'Technology',
    'Engineering',
    'Finance',
    'Marketing',
    'Sales',
    'Human Resources',
    'Operations',
    'Customer Service',
    'Design',
    'Data Science',
    'Research',
    'Healthcare',
    'Education',
    'Legal',
    'Other'
  ];

  // Common benefits
  const commonBenefits = [
    'Medical Aid',
    'Dental Insurance',
    'Retirement Fund',
    'Flexible Working Hours',
    'Remote Work Options',
    'Professional Development',
    'Gym Membership',
    'Transport Allowance',
    'Meal Allowance',
    'Annual Bonus',
    'Performance Bonus',
    'Study Assistance',
    'Paid Time Off',
    '13th Cheque'
  ];

  // Initialize form with existing job data
  useEffect(() => {
    if (existingJob) {
      setFormData({
        title: existingJob.title || '',
        description: existingJob.description || '',
        requirements: {
          education: existingJob.requirements?.education || '',
          experience: existingJob.requirements?.experience || '',
          skills: existingJob.requirements?.skills || [],
          other: existingJob.requirements?.other || ''
        },
        location: existingJob.location || '',
        jobType: existingJob.jobType || 'full-time',
        experienceLevel: existingJob.experienceLevel || 'entry-level',
        salaryRange: {
          min: existingJob.salaryRange?.min || '',
          max: existingJob.salaryRange?.max || '',
          currency: existingJob.salaryRange?.currency || 'ZAR',
          period: existingJob.salaryRange?.period || 'monthly'
        },
        benefits: existingJob.benefits || [],
        applicationDeadline: existingJob.applicationDeadline ? 
          new Date(existingJob.applicationDeadline).toISOString().split('T')[0] : '',
        startDate: existingJob.startDate ? 
          new Date(existingJob.startDate).toISOString().split('T')[0] : '',
        isRemote: existingJob.isRemote || false,
        category: existingJob.category || '',
        department: existingJob.department || '',
        contactEmail: existingJob.contactEmail || companyProfile?.email || '',
        applicationInstructions: existingJob.applicationInstructions || '',
        companyDescription: existingJob.companyDescription || companyProfile?.description || '',
        tags: existingJob.tags || []
      });
    } else if (companyProfile) {
      // Pre-fill with company data for new jobs
      setFormData(prev => ({
        ...prev,
        contactEmail: companyProfile.email || '',
        companyDescription: companyProfile.description || '',
        location: companyProfile.location || ''
      }));
    }
  }, [existingJob, companyProfile]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
    
    // Clear errors when typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  // Handle array inputs (skills, benefits, tags)
  const handleArrayAdd = (field, value) => {
    if (value.trim() && !formData[field].includes(value.trim())) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
    }
  };

  const handleArrayRemove = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  // Handle nested array inputs (requirements.skills)
  const handleNestedArrayAdd = (parent, field, value) => {
    if (value.trim() && !formData[parent][field].includes(value.trim())) {
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [field]: [...prev[parent][field], value.trim()]
        }
      }));
    }
  };

  const handleNestedArrayRemove = (parent, field, index) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: prev[parent][field].filter((_, i) => i !== index)
      }
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.title.trim()) {
      newErrors.title = 'Job title is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Job description is required';
    } else if (formData.description.length < 100) {
      newErrors.description = 'Job description must be at least 100 characters';
    }
    
    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }
    
    if (!formData.category) {
      newErrors.category = 'Job category is required';
    }
    
    if (!formData.contactEmail.trim()) {
      newErrors.contactEmail = 'Contact email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Invalid email address';
    }
    
    // Application deadline validation
    if (formData.applicationDeadline) {
      const deadline = new Date(formData.applicationDeadline);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (deadline < today) {
        newErrors.applicationDeadline = 'Application deadline must be in the future';
      }
    }
    
    // Salary range validation
    if (formData.salaryRange.min && formData.salaryRange.max) {
      const min = parseFloat(formData.salaryRange.min);
      const max = parseFloat(formData.salaryRange.max);
      
      if (min >= max) {
        newErrors['salaryRange.max'] = 'Maximum salary must be greater than minimum';
      }
    }
    
    return newErrors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isVerified) {
      setErrors({ general: 'Company account must be verified to post jobs' });
      return;
    }
    
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setLoading(true);
    try {
      // Prepare job data
      const jobData = {
        ...formData,
        company: {
          id: companyProfile.id,
          name: companyProfile.companyName,
          logo: companyProfile.logoUrl,
          location: companyProfile.location,
          industry: companyProfile.industry
        },
        applicationDeadline: formData.applicationDeadline ? 
          new Date(formData.applicationDeadline) : null,
        startDate: formData.startDate ? 
          new Date(formData.startDate) : null,
        salaryRange: {
          ...formData.salaryRange,
          min: formData.salaryRange.min ? parseFloat(formData.salaryRange.min) : null,
          max: formData.salaryRange.max ? parseFloat(formData.salaryRange.max) : null
        }
      };
      
      let jobId;
      if (existingJob) {
        await updateJobPosting(existingJob.id, jobData, companyProfile.id);
        jobId = existingJob.id;
      } else {
        jobId = await createJobPosting(jobData, companyProfile.id);
      }
      
      if (onSaved) {
        onSaved(jobId);
      } else {
        navigate('/company/jobs');
      }
    } catch (error) {
      console.error('Error saving job posting:', error);
      setErrors({ general: error.message || 'Failed to save job posting' });
    } finally {
      setLoading(false);
    }
  };

  // Component for array input fields
  const ArrayInputField = ({ 
    label, 
    field, 
    placeholder, 
    items, 
    onAdd, 
    onRemove,
    suggestions = [] 
  }) => {
    const [inputValue, setInputValue] = useState('');
    
    const handleAdd = () => {
      if (inputValue.trim()) {
        onAdd(inputValue.trim());
        setInputValue('');
      }
    };
    
    const handleKeyPress = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAdd();
      }
    };
    
    return (
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        <div className="flex space-x-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
          <button
            type="button"
            onClick={handleAdd}
            className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
        
        {/* Suggestions */}
        {suggestions.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                onClick={() => onAdd(suggestion)}
                disabled={items.includes(suggestion)}
                className={`px-2 py-1 text-xs rounded-full border ${
                  items.includes(suggestion)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border-gray-300'
                }`}
              >
                + {suggestion}
              </button>
            ))}
          </div>
        )}
        
        {/* Selected items */}
        {items.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {items.map((item, index) => (
              <span
                key={index}
                className="inline-flex items-center px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm"
              >
                {item}
                <button
                  type="button"
                  onClick={() => onRemove(index)}
                  className="ml-2 text-green-500 hover:text-green-700"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (!isVerified) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
          <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-yellow-800 mb-2">
            Account Verification Required
          </h3>
          <p className="text-yellow-700 mb-4">
            Your company account must be verified before you can post job opportunities. 
            Please wait for our team to review and verify your account.
          </p>
          <button
            onClick={() => navigate('/company/dashboard')}
            className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">
                  {existingJob ? 'Edit Job Posting' : 'Create Job Posting'}
                </h1>
                <p className="text-gray-600">
                  {existingJob ? 'Update your job posting details' : 'Post a new job opportunity to attract top talent'}
                </p>
              </div>
            </div>
            {isVerified && (
              <div className="flex items-center space-x-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                <CheckCircle className="w-4 h-4" />
                <span>Verified Company</span>
              </div>
            )}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          {/* Basic Information */}
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center space-x-2">
              <Target className="w-5 h-5 text-green-500" />
              <span>Basic Information</span>
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Job Title */}
              <div className="md:col-span-2 space-y-2">
                <label className="text-sm font-medium text-gray-700">Job Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="e.g. Senior Software Developer"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.title ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.title && (
                  <p className="text-red-500 text-sm">{errors.title}</p>
                )}
              </div>

              {/* Job Category */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Category *</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.category ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select Category</option>
                  {jobCategories.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
                {errors.category && (
                  <p className="text-red-500 text-sm">{errors.category}</p>
                )}
              </div>

              {/* Department */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Department</label>
                <input
                  type="text"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  placeholder="e.g. Engineering"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              {/* Job Type */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Job Type</label>
                <select
                  name="jobType"
                  value={formData.jobType}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  {jobTypes.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {/* Experience Level */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Experience Level</label>
                <select
                  name="experienceLevel"
                  value={formData.experienceLevel}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  {experienceLevels.map((level) => (
                    <option key={level.value} value={level.value}>{level.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Location */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Location *</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MapPin className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="e.g. Cape Town, South Africa"
                    className={`pl-10 pr-4 py-2 w-full border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      errors.location ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                </div>
                {errors.location && (
                  <p className="text-red-500 text-sm">{errors.location}</p>
                )}
              </div>

              <div className="flex items-center space-x-3 pt-6">
                <input
                  type="checkbox"
                  id="isRemote"
                  name="isRemote"
                  checked={formData.isRemote}
                  onChange={handleChange}
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <label htmlFor="isRemote" className="text-sm font-medium text-gray-700">
                  Remote work available
                </label>
              </div>
            </div>
          </div>

          {/* Job Description */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center space-x-2">
              <BookOpen className="w-5 h-5 text-green-500" />
              <span>Job Description</span>
            </h2>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Description *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Provide a detailed description of the role, responsibilities, and what makes this opportunity exciting..."
                rows={8}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none ${
                  errors.description ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              <div className="flex justify-between items-center">
                {errors.description && (
                  <p className="text-red-500 text-sm">{errors.description}</p>
                )}
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.description.length} characters (minimum 100)
                </p>
              </div>
            </div>
          </div>

          {/* Requirements */}
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center space-x-2">
              <Users className="w-5 h-5 text-green-500" />
              <span>Requirements & Qualifications</span>
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Education Requirements</label>
                <textarea
                  name="requirements.education"
                  value={formData.requirements.education}
                  onChange={handleChange}
                  placeholder="e.g. Bachelor's degree in Computer Science or related field"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Experience Requirements</label>
                <textarea
                  name="requirements.experience"
                  value={formData.requirements.experience}
                  onChange={handleChange}
                  placeholder="e.g. 3+ years of experience in software development"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                />
              </div>
            </div>

            {/* Skills */}
            <ArrayInputField
              label="Required Skills"
              field="skills"
              placeholder="Add a skill (e.g. React, Python, Project Management)"
              items={formData.requirements.skills}
              onAdd={(skill) => handleNestedArrayAdd('requirements', 'skills', skill)}
              onRemove={(index) => handleNestedArrayRemove('requirements', 'skills', index)}
              suggestions={['JavaScript', 'Python', 'React', 'Node.js', 'SQL', 'Project Management', 'Communication', 'Leadership']}
            />

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Additional Requirements</label>
              <textarea
                name="requirements.other"
                value={formData.requirements.other}
                onChange={handleChange}
                placeholder="Any other specific requirements or preferences..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
              />
            </div>
          </div>

          {/* Compensation & Benefits */}
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center space-x-2">
              <DollarSign className="w-5 h-5 text-green-500" />
              <span>Compensation & Benefits</span>
            </h2>
            
            {/* Salary Range */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Min Salary</label>
                <input
                  type="number"
                  name="salaryRange.min"
                  value={formData.salaryRange.min}
                  onChange={handleChange}
                  placeholder="25000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Max Salary</label>
                <input
                  type="number"
                  name="salaryRange.max"
                  value={formData.salaryRange.max}
                  onChange={handleChange}
                  placeholder="35000"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors['salaryRange.max'] ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors['salaryRange.max'] && (
                  <p className="text-red-500 text-sm">{errors['salaryRange.max']}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Currency</label>
                <select
                  name="salaryRange.currency"
                  value={formData.salaryRange.currency}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="ZAR">ZAR (R)</option>
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                </select>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Period</label>
                <select
                  name="salaryRange.period"
                  value={formData.salaryRange.period}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="monthly">Monthly</option>
                  <option value="annually">Annually</option>
                  <option value="hourly">Hourly</option>
                </select>
              </div>
            </div>

            {/* Benefits */}
            <ArrayInputField
              label="Benefits & Perks"
              field="benefits"
              placeholder="Add a benefit"
              items={formData.benefits}
              onAdd={(benefit) => handleArrayAdd('benefits', benefit)}
              onRemove={(index) => handleArrayRemove('benefits', index)}
              suggestions={commonBenefits}
            />
          </div>

          {/* Application Details */}
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center space-x-2">
              <Calendar className="w-5 h-5 text-green-500" />
              <span>Application Details</span>
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Application Deadline</label>
                <input
                  type="date"
                  name="applicationDeadline"
                  value={formData.applicationDeadline}
                  onChange={handleChange}
                  min={new Date().toISOString().split('T')[0]}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.applicationDeadline ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.applicationDeadline && (
                  <p className="text-red-500 text-sm">{errors.applicationDeadline}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Expected Start Date</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Contact Email *</label>
              <input
                type="email"
                name="contactEmail"
                value={formData.contactEmail}
                onChange={handleChange}
                placeholder="hr@company.com"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  errors.contactEmail ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.contactEmail && (
                <p className="text-red-500 text-sm">{errors.contactEmail}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Application Instructions</label>
              <textarea
                name="applicationInstructions"
                value={formData.applicationInstructions}
                onChange={handleChange}
                placeholder="Provide specific instructions for applicants (e.g. portfolio requirements, cover letter topics, etc.)"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
              />
            </div>
          </div>

          {/* Company Information */}
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center space-x-2">
              <Building className="w-5 h-5 text-green-500" />
              <span>Company Information</span>
            </h2>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Company Description</label>
              <textarea
                name="companyDescription"
                value={formData.companyDescription}
                onChange={handleChange}
                placeholder="Brief description of your company, culture, and what makes it a great place to work..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
              />
              <p className="text-xs text-gray-500">
                This will be displayed alongside the job posting to give candidates insight into your company.
              </p>
            </div>

            {/* Tags */}
            <ArrayInputField
              label="Job Tags"
              field="tags"
              placeholder="Add a tag (e.g. startup, flexible, growth)"
              items={formData.tags}
              onAdd={(tag) => handleArrayAdd('tags', tag)}
              onRemove={(index) => handleArrayRemove('tags', index)}
              suggestions={['Startup', 'Enterprise', 'Remote-friendly', 'Fast-paced', 'Growth opportunity', 'Flexible hours', 'Mentorship', 'Innovation']}
            />
          </div>

          {/* General Error */}
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                <div>
                  <p className="text-red-800 font-medium">Error</p>
                  <p className="text-red-700 text-sm">{errors.general}</p>
                </div>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex items-center justify-between pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel || (() => navigate('/company/jobs'))}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                {existingJob ? 'Update job posting' : 'Create and publish job posting'}
              </div>
              <button
                type="submit"
                disabled={loading}
                className={`px-6 py-2 rounded-lg font-medium flex items-center space-x-2 transition-colors ${
                  loading
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-green-500 hover:bg-green-600 text-white'
                }`}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>{existingJob ? 'Updating...' : 'Creating...'}</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>{existingJob ? 'Update Job' : 'Create Job'}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JobPostingForm;